import { assign, createMachine } from "xstate";

interface IContext {
  variant: "select" | "combo";
  items: unknown[];
  isDisabled: boolean;
  multiple: boolean;
  openOnFocus: boolean;
  currentValues: Array<unknown>;
  inputValue: string;
  activeListIndex: number;
  itemComparator: (firs: unknown, second: unknown) => boolean;
  inputDisplayValue: (values: unknown[]) => string;
}

type IEvent =
  | { type: "INPUT.CHANGE"; value: string }
  | { type: "ITEMS.SET"; value: unknown[] }
  | { type: "ITEMS.SELECT"; value: unknown }
  | { type: "ITEMS.SELECT_ACTIVE_INDEX" }
  | { type: "RESET" }
  | { type: "DISABLE" }
  | { type: "ENABLE" }
  | { type: "DEACTIVATE" }
  | { type: "INPUT.FOCUS" }
  | { type: "INPUT.BLUR" }
  | { type: "TRIGGER.FOCUS" }
  | { type: "TRIGGER.BLUR" }
  | { type: "LIST.SELECT_NEXT_INDEX" }
  | { type: "LIST.SELECT_PREV_INDEX" }
  | { type: "LIST.SELECT_FIRST" }
  | { type: "LIST.SELECT_LAST" }
  | { type: "LIST.CLOSE" }
  | { type: "LIST.OPEN" }
  | { type: "TOGGLE" };

export const machine = createMachine(
  {
    initial: "inactive",
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    preserveActionOrder: true,
    context: {
      isDisabled: false,
      variant: "combo",
      items: [],
      openOnFocus: false,
      multiple: false,
      currentValues: [],
      inputValue: "",
      activeListIndex: 0,
      itemComparator: (first, second) => {
        return first === second;
      },
      inputDisplayValue: (_: unknown) => {
        return "";
      },
    },
    on: {
      "ITEMS.SET": {
        actions: ["setItems"],
      },
      RESET: {
        actions: [
          "setInputValue",
          "selectItem",
          "triggerOnInputValueChange",
          "triggerOnItemSelect",
          "focusInputInDOM",
          "focusTriggerInDOM",
        ],
      },
      DEACTIVATE: {
        target: "inactive",
        actions: ["setInputValue"],
      },
    },
    states: {
      inactive: {
        always: [
          {
            target: "disabled",
            actions: ["updateDisabledState"],
            cond: "isDisabled",
          },
        ],
        on: {
          DISABLE: {
            target: "disabled",
          },
          "TRIGGER.FOCUS": {
            target: "expanded",
          },
          "INPUT.FOCUS": [
            {
              target: "expanded",
              cond: "shouldOpenOnFocus",
            },
            {
              target: "collapsed",
            },
          ],
          TOGGLE: {
            actions: ["focusInputInDOM"],
            target: "expanded",
          },
        },
      },
      disabled: {
        on: {
          ENABLE: {
            actions: ["updateDisabledState"],
            target: "inactive",
          },
        },
      },
      expanded: {
        tags: ["active"],
        on: {
          TOGGLE: {
            target: "collapsed",
          },
          "LIST.CLOSE": {
            target: "collapsed",
          },
          "LIST.SELECT_NEXT_INDEX": {
            actions: ["setActiveIndexOnNextItem", "triggerListIndexChange"],
          },
          "LIST.SELECT_PREV_INDEX": {
            actions: ["setActiveIndexOnPrevItem", "triggerListIndexChange"],
          },
          "ITEMS.SELECT_ACTIVE_INDEX": [
            {
              actions: ["selectItem", "setInputValue", "triggerOnItemSelect"],
              cond: "isMultiple",
            },
            {
              actions: ["selectItem", "setInputValue", "triggerOnItemSelect"],
              target: "collapsed",
            },
          ],
          "ITEMS.SELECT": [
            {
              actions: ["selectItem", "setInputValue", "triggerOnItemSelect"],
              cond: "isMultiple",
            },
            {
              actions: ["selectItem", "setInputValue", "triggerOnItemSelect"],
              target: "collapsed",
            },
          ],
          "LIST.SELECT_LAST": {
            actions: ["setIndexOnLastItem", "triggerListIndexChange"],
          },
          "LIST.SELECT_FIRST": {
            actions: ["setIndexOnFirstItem", "triggerListIndexChange"],
          },
          "INPUT.CHANGE": {
            actions: ["setInputValue", "triggerOnInputValueChange"],
          },
        },
      },
      collapsed: {
        tags: ["active"],
        on: {
          TOGGLE: {
            actions: ["focusInputInDOM"],
            target: "expanded",
          },
          "LIST.OPEN": {
            target: "expanded",
          },
          "LIST.CLOSE": {
            target: "inactive",
            actions: ["blurInputInDOM", "blurTriggerInDOM"],
          },
          "INPUT.CHANGE": {
            actions: ["setInputValue", "triggerOnInputValueChange"],
            target: "expanded",
          },
          "LIST.SELECT_NEXT_INDEX": {
            target: "expanded",
          },
          "INPUT.BLUR": {
            target: "inactive",
          },
          "TRIGGER.BLUR": {
            target: "inactive",
          },
          "ITEMS.SELECT_ACTIVE_INDEX": {
            target: "inactive",
            actions: ["blurInputInDOM", "blurTriggerInDOM"],
          },
        },
      },
    },
  },
  {
    guards: {
      isDisabled: (context) => {
        return context.isDisabled;
      },
      shouldOpenOnFocus: (context) => {
        return context.openOnFocus;
      },
      isMultiple: (context) => {
        return context.multiple;
      },
    },
    actions: {
      updateDisabledState: assign<IContext, IEvent>({
        isDisabled: (context, event) => {
          if (event.type === "ENABLE") {
            return false;
          } else if (event.type === "DISABLE") {
            return true;
          }
          return context.isDisabled;
        },
      }),
      setIndexOnLastItem: assign<IContext, IEvent>({
        activeListIndex: (context) => {
          return context.items.length - 1;
        },
      }),
      setIndexOnFirstItem: assign<IContext, IEvent>({
        activeListIndex: (_) => {
          return 0;
        },
      }),
      setItems: assign<IContext, IEvent>({
        items: (context, event) => {
          if (event.type === "ITEMS.SET") {
            return event.value;
          }
          return context.items;
        },
        activeListIndex: 0,
      }),
      selectItem: assign<IContext, IEvent>({
        currentValues: (context, event) => {
          if (event.type === "RESET") {
            return [];
          }
          if (
            event.type === "ITEMS.SELECT_ACTIVE_INDEX" ||
            event.type === "ITEMS.SELECT"
          ) {
            const selectedItem =
              event.type === "ITEMS.SELECT_ACTIVE_INDEX"
                ? context.items[context.activeListIndex]
                : event.value;

            if (!selectedItem) {
              return context.currentValues;
            }

            const isAlreadySelected = context.currentValues.some((item) => {
              return context.itemComparator(selectedItem, item);
            });

            if (context.multiple) {
              if (isAlreadySelected) {
                return context.currentValues.filter((item) => {
                  return !context.itemComparator(selectedItem, item);
                });
              } else {
                return [...context.currentValues, selectedItem];
              }
            } else {
              return [selectedItem];
            }
          }
          return context.currentValues;
        },
      }),

      setActiveIndexOnNextItem: assign<IContext, IEvent>({
        activeListIndex: (context) => {
          return (context.activeListIndex + 1) % context.items.length;
        },
      }),
      setActiveIndexOnPrevItem: assign<IContext, IEvent>({
        activeListIndex: (context) => {
          const next = context.activeListIndex - 1;
          if (next === -1) {
            return context.items.length - 1;
          }
          return next;
        },
      }),
      setInputValue: assign<IContext, IEvent>({
        inputValue: (context, event) => {
          if (event.type === "RESET") {
            return "";
          }
          if (event.type === "DEACTIVATE") {
            return (
              context.inputDisplayValue(context.currentValues) ||
              context.inputValue
            );
          }
          if (event.type === "INPUT.CHANGE") {
            return event.value;
          }
          if (event.type === "ITEMS.SELECT") {
            return context.inputDisplayValue(context.currentValues);
          }
          if (event.type === "ITEMS.SELECT_ACTIVE_INDEX") {
            return context.inputDisplayValue(context.currentValues);
          }
          return context.inputValue;
        },
      }),
    },
  }
);
